import { isMapGeometry, MapGeometry } from 'src/app/models/geometry.model';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Observable,
  finalize,
  catchError,
  map,
  of,
  shareReplay,
  throwError
} from 'rxjs';
import {
  GeometryData,
  RiskOverlapRow,
  RiskValueAggregate,
  TileUrlsResponse
} from 'src/app/models/crd-state.interface';
import { DEFAULT_MAP_STYLE } from 'src/app/constants/map.const';
import { materializedRiskColumn } from 'src/app/constants/risk-column-alias.const';
import { environment } from 'src/environments/environment';
import {
  GeometryPayload,
  RiskValueAggregatePayload
} from 'src/app/models/geometry-payload.model';
import { PagedResponse } from 'src/app/models/paged-response.model';
import { RiskValueTableParams } from 'src/app/models/table-params.model';
import { EventStateService } from '../state-service/event-state.service';

type GeometryResponse = PagedResponse<{
  plantation_code: string;
  _geo: string | null;
}>;

@Injectable({
  providedIn: 'root'
})
export class MapPageService {
  private readonly tileUrlsCacheTtlMs = 60 * 60 * 1000;
  private readonly tileUrlsCache = new Map<
    string,
    { request$: Observable<TileUrlsResponse>; expiresAt: number }
  >();

  constructor(
    private http: HttpClient,
    private eventStateService: EventStateService
  ) {}

  getTileUrls(
    riskIds: string[],
    cacheScope = ''
  ): Observable<TileUrlsResponse> {
    const cacheKey = this.getTileUrlsCacheKey(riskIds, cacheScope);
    const cachedEntry = this.tileUrlsCache.get(cacheKey);

    if (cachedEntry && cachedEntry.expiresAt > Date.now()) {
      return cachedEntry.request$;
    }

    if (cachedEntry) {
      this.tileUrlsCache.delete(cacheKey);
    }

    const request$ = this.fetchTileUrls(riskIds).pipe(
      catchError((error: unknown) => {
        this.tileUrlsCache.delete(cacheKey);
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.tileUrlsCache.set(cacheKey, {
      request$,
      expiresAt: Date.now() + this.tileUrlsCacheTtlMs
    });
    return request$;
  }

  private fetchTileUrls(riskIds: string[]): Observable<TileUrlsResponse> {
    return this.http.post<TileUrlsResponse>(
      `${environment.CRD_API}v2/layers/tile-urls`,
      { risk_ids: riskIds }
    );
  }

  private getTileUrlsCacheKey(riskIds: string[], cacheScope: string): string {
    return `${cacheScope}:${[...riskIds].sort().join('|')}`;
  }

  getGeometryData(
    params: GeometryPayload,
    isSiLinking = false
  ): Observable<GeometryData[]> {
    this.eventStateService.isGeometryDataLoading = true;
    const geoQuery = isSiLinking
      ? 'ST_ASGEOJSON(_geo) as _geo'
      : 'ST_ASGEOJSON(IFNULL(risk_buffer_geo, _geo)) as _geo';
    const body = {
      columns: {
        schema_version: 'v1',
        columns: [geoQuery, 'plantation_code']
      },
      AND: {
        plantation_code: params.plantationCodes
      },
      OR: {}
    };

    return this.http
      .post<GeometryResponse>(
        `${environment.CRD_API}v2/plantations/?date=${params.period}&page=1&page_size=10000`,
        body
      )
      .pipe(
        map((geometry) =>
          geometry.results.map((geo) => ({
            ...geo,
            _geo: this.parseGeometry(geo._geo)
          }))
        ),
        finalize(() => {
          this.eventStateService.isGeometryDataLoading = false;
        })
      );
  }

  private parseGeometry(value: string | null): MapGeometry | undefined {
    // AgriTrace allows saving before mapping. Older clients also saved an empty
    // Polygon ([[]]); retain those rows without passing a shape to map consumers.
    // TODO: Stop unmapped AgriTrace plots propagating into geospatial datasets in
    // crd-sync-utils, while retaining plantation records for survey access.
    if (value === null) return undefined;
    try {
      const geometry: unknown = JSON.parse(value);
      return isMapGeometry(geometry) ? geometry : undefined;
    } catch {
      return undefined;
    }
  }

  getMapStyle() {
    return of(DEFAULT_MAP_STYLE);
  }

  getPagedRiskValues(params: RiskValueTableParams) {
    const orderBy = params?.orderBy
      ? `&order_by=${params.orderBy}`
      : '&order_by=-percentage';

    const riskColumn = materializedRiskColumn(params.riskType);
    const body = {
      columns: {
        schema_version: 'v1',
        columns: [
          `${riskColumn}_overlap_area as area`,
          `round(${riskColumn}_overlap_area_perc, 6)  as percentage`,
          'plantation_name',
          'plantation_code'
        ]
      },
      AND: {
        ...this.getPlantationCodeQuery(params.plantationCodes),
        [`${riskColumn}_value`]: [true]
      },
      OR: {}
    };

    return this.http.post<PagedResponse<RiskOverlapRow>>(
      `${environment.CRD_API}v2/plantations/?date=${params.period}&page=${params.page}&page_size=${params.pageSize}${orderBy}`,
      body
    );
  }

  getRiskValueAggregates(
    params: RiskValueAggregatePayload
  ): Observable<RiskValueAggregate[]> {
    const riskColumn = materializedRiskColumn(params.riskType);
    const body = {
      aggregates: [
        {
          key: `${riskColumn}_overlap_area`,
          function: 'sum',
          alias: 'area_calculated_sum'
        },
        {
          key: 'plantation_code',
          function: 'count'
        }
      ],
      AND: {
        ...this.getPlantationCodeQuery(params.plantationCodes),
        [`${riskColumn}_value`]: [true]
      },
      OR: {}
    };

    return this.http.post<RiskValueAggregate[]>(
      `${environment.CRD_API}v2/statistics/dynamic/stats/`,
      body
    );
  }

  getPlantationCodeQuery(plantationCodes: string[]) {
    // empty plantation used to return nothing
    return plantationCodes.length
      ? { plantation_code: plantationCodes }
      : { plantation_code: ['empty plantation'] };
  }
}
